import { useEffect, useRef, useState } from 'react';
import styles from './DocUpload.module.css';

/**
 * DocUpload — premium document upload with thumbnail preview + lightbox.
 *
 * Exports:
 *   <DocCard label required value onChange onView disabled />
 *     One document slot: empty drop-zone → uploading → thumbnail + actions.
 *     value is { name, path, url? } (or a bare url string, tolerated), or null.
 *   <Lightbox file onClose />
 *     Full-screen preview of an image or PDF.
 *
 * Uploads hit POST /api/v1/uploads/document (returns { path, fileName }).
 * Documents are SECURE — there is no public url; a preview is fetched from
 * GET /api/v1/uploads/document?path=... (authenticated) as a blob and shown via
 * an object URL. No API change.
 */

const API_BASE = import.meta.env.VITE_API_BASE || 'http://127.0.0.1:8000';
const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf';

const stripQuery = (s = '') => String(s).split('?')[0];
const extOf = (s = '') => (stripQuery(s).split('.').pop() || '').toLowerCase();
const isImageRef = (ref = '') => /\.(png|jpe?g|webp|gif|svg|bmp)$/i.test(stripQuery(ref));
const isPdfRef = (ref = '') => /\.pdf$/i.test(stripQuery(ref));

// Normalise a stored value (object or bare string) into { name, path, url }.
function normalise(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  return { name: value, url: value };
}

// Best string to detect the file type from (filename, else path, else url).
const typeRef = (f) => f?.name || f?.path || f?.url || '';

async function uploadDocument(file) {
  if (file.size > MAX_BYTES) throw new Error('File is over 8 MB — please upload a smaller one.');
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error('Could not read that file.'));
    r.readAsDataURL(file);
  });
  const token = localStorage.getItem('1ayurved_token');
  const res = await fetch(`${API_BASE}/api/v1/uploads/document`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ data: dataUrl, filename: file.name }),
  });
  if (!res.ok) {
    let msg = `Upload failed (${res.status})`;
    try { const j = await res.json(); if (j.message) msg = j.message; } catch { /* non-JSON */ }
    throw new Error(msg);
  }
  const out = await res.json(); // { path, fileName } (no public url)
  return { name: out.fileName || file.name, path: out.path, url: out.url };
}

/**
 * Resolve a viewable (object) URL for a secure document.
 * - If the value already carries a public `url`, use it directly.
 * - Otherwise fetch the blob from the authenticated endpoint by `path`.
 * Only runs when `enabled` is true (e.g. an image thumbnail, or an open lightbox),
 * so PDFs/other files are not downloaded until actually viewed.
 */
function useViewUrl(file, enabled) {
  const [url, setUrl] = useState(null);
  const path = file && typeof file === 'object' ? file.path : null;
  const direct = file && typeof file === 'object' ? file.url : (typeof file === 'string' ? file : null);

  useEffect(() => {
    if (!enabled) { setUrl(null); return undefined; }
    if (direct) { setUrl(direct); return undefined; }
    if (!path) { setUrl(null); return undefined; }

    let cancelled = false;
    let objUrl = null;
    (async () => {
      try {
        const token = localStorage.getItem('1ayurved_token');
        const res = await fetch(`${API_BASE}/api/v1/uploads/document?path=${encodeURIComponent(path)}`, {
          headers: { Accept: '*/*', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (!res.ok) throw new Error(`Preview failed (${res.status})`);
        const blob = await res.blob();
        objUrl = URL.createObjectURL(blob);
        if (!cancelled) setUrl(objUrl);
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();

    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [enabled, path, direct]);

  return url;
}

export function DocCard({ label, required = false, value, onChange, onView, disabled = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [over, setOver] = useState(false);

  const val = normalise(value);
  const ref = typeRef(val);
  const isImg = !!val && isImageRef(ref);
  const isPdf = !!val && isPdfRef(ref);

  // Fetch a preview URL eagerly only for images (for the thumbnail).
  const thumbUrl = useViewUrl(val, isImg);

  const handleFile = async (file) => {
    if (!file) return;
    setErr('');
    setBusy(true);
    try {
      const out = await uploadDocument(file);
      onChange(out);
    } catch (e) {
      setErr(e?.message || 'Upload failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const pick = () => { if (!disabled && !busy) inputRef.current?.click(); };
  const onInput = (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleFile(f); };
  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    if (disabled || busy) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <div className={`${styles.card} ${val ? styles.cardDone : ''}`}>
      <div className={`${styles.label} ${required ? styles.req : ''}`}>{label}</div>

      {busy ? (
        <div className={styles.uploading}>
          <div className={styles.spinner} />
          <span>Uploading…</span>
        </div>
      ) : !val ? (
        <div
          className={`${styles.drop} ${over ? styles.dropOver : ''}`}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={`Upload ${label}`}
          onClick={pick}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } }}
          onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
        >
          <i className={`bi bi-cloud-arrow-up ${styles.dropIcon}`} aria-hidden="true" />
          <span className={styles.dropText}>Drop file or click</span>
          <span className={styles.dropHint}>PDF, PNG, JPG · up to 8 MB</span>
        </div>
      ) : (
        <>
          <div
            className={styles.thumbWrap}
            role="button"
            tabIndex={0}
            title="View"
            onClick={() => onView?.(val)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onView?.(val); } }}
          >
            {isImg && thumbUrl ? (
              <img className={styles.thumbImg} src={thumbUrl} alt={val.name || label} />
            ) : (
              <div className={styles.thumbFile}>
                <i
                  className={`bi ${isPdf ? 'bi-file-earmark-pdf ' + styles.pdf : 'bi-file-earmark-text ' + styles.docgen} ${styles.thumbFileIcon}`}
                  aria-hidden="true"
                />
                <span className={styles.thumbFileExt}>{extOf(ref) || 'file'}</span>
              </div>
            )}
            <span className={styles.doneTag}><i className="bi bi-check-lg" aria-hidden="true" /> Uploaded</span>
            <div className={styles.thumbOverlay}><i className="bi bi-eye" aria-hidden="true" /> View</div>
          </div>

          <div className={styles.fileName} title={val.name}>{val.name}</div>

          <div className={styles.actions}>
            <button type="button" className={styles.act} onClick={pick} disabled={disabled}>
              <i className="bi bi-arrow-repeat" aria-hidden="true" /> Replace
            </button>
            <button type="button" className={`${styles.act} ${styles.actRemove}`} onClick={() => onChange(null)} disabled={disabled}>
              <i className="bi bi-trash" aria-hidden="true" /> Remove
            </button>
          </div>
        </>
      )}

      {err && <div className={styles.err}>{err}</div>}
      <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={onInput} disabled={disabled} />
    </div>
  );
}

export function Lightbox({ file, onClose }) {
  const f = normalise(file);
  const ref = typeRef(f);
  const isImg = !!f && isImageRef(ref);
  const isPdf = !!f && isPdfRef(ref);
  // Resolve the viewable URL whenever the lightbox is open.
  const url = useViewUrl(f, !!f);

  useEffect(() => {
    if (!file) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [file, onClose]);

  if (!f) return null;

  return (
    <div className={styles.lbOverlay} role="dialog" aria-modal="true" aria-label={f.name || 'Document preview'} onClick={onClose}>
      <div className={styles.lbBar} onClick={(e) => e.stopPropagation()}>
        <span className={styles.lbName}>{f.name}</span>
        <div className={styles.lbBtns}>
          {url && (
            <a className={styles.lbBtn} href={url} target="_blank" rel="noreferrer">
              <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> Open
            </a>
          )}
          <button type="button" className={styles.lbBtn} onClick={onClose}>
            <i className="bi bi-x-lg" aria-hidden="true" /> Close
          </button>
        </div>
      </div>
      <div className={styles.lbStage} onClick={(e) => e.stopPropagation()}>
        {!url ? (
          <div className={styles.lbFallback}>Loading preview…</div>
        ) : isImg ? (
          <img className={styles.lbImg} src={url} alt={f.name || 'Document'} />
        ) : isPdf ? (
          <iframe className={styles.lbFrame} src={url} title={f.name || 'Document preview'} />
        ) : (
          <div className={styles.lbFallback}>
            Preview not available. <a href={url} target="_blank" rel="noreferrer">Open the file</a>.
          </div>
        )}
      </div>
    </div>
  );
}
